/** JWT helpers — decode payload only (no signature verification). Safe for display and UX; never for server authorization. */

const JWT_PARTS = 3;
const MAX_JWT_LENGTH = 8192;

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  const base64 = padded + '='.repeat(padLen);
  if (typeof atob !== 'function') {
    return '';
  }
  try {
    const binary = atob(base64);
    if (typeof TextDecoder !== 'undefined') {
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    }
    return decodeURIComponent(
      Array.from(binary)
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );
  } catch {
    return '';
  }
}

/** Returns parsed JWT payload or null if the token is malformed. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (!token || token.length > MAX_JWT_LENGTH) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length !== JWT_PARTS || !parts[1]) {
    return null;
  }
  try {
    const json = base64UrlDecode(parts[1]);
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Normalize JWT `exp` to Unix seconds (some issuers use milliseconds). */
function expClaimToUnixSeconds(exp: number): number {
  return exp > 1e12 ? Math.floor(exp / 1000) : exp;
}

function readExpClaim(payload: Record<string, unknown>): number | null {
  const exp = payload['exp'];
  if (typeof exp === 'number' && Number.isFinite(exp)) {
    return expClaimToUnixSeconds(exp);
  }
  if (typeof exp === 'string' && exp.trim()) {
    const parsed = Number(exp);
    return Number.isFinite(parsed) ? expClaimToUnixSeconds(parsed) : null;
  }
  return null;
}

/** Numeric `exp` claim in seconds since epoch, if present. */
export function jwtExpiryUnix(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }
  return readExpClaim(payload);
}

/**
 * Whether the token is a decodable JWT suitable for client-side session UX.
 * Expiry is not checked here — the API and refresh flow handle that (see remmi-v16 `isTokenValid`).
 */
export function isRecognizedAccessToken(token: string): boolean {
  if (!token || token.split('.').length !== JWT_PARTS) {
    return false;
  }
  return decodeJwtPayload(token) !== null;
}

/**
 * Whether the access token is past `exp` (with optional clock skew). Client-side only.
 * Tokens without an `exp` claim are treated as not client-expired; the API remains the source of truth.
 */
export function isJwtExpired(token: string, skewSeconds = 30): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return true;
  }
  const exp = readExpClaim(payload);
  if (exp === null) {
    return false;
  }
  const now = Math.floor(Date.now() / 1000);
  return exp <= now + skewSeconds;
}

/** Walk dotted paths like `realm_access.roles`. */
function readClaim(payload: Record<string, unknown>, path: string): unknown {
  const segments = path.split('.').filter(Boolean);
  let current: unknown = payload;
  for (const key of segments) {
    if (current === null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/**
 * Normalize roles claim to a string array (supports `roles`, single `role`, or nested paths).
 * Client-side only: use for menus, directives, and route UX. Privileged operations must still be
 * enforced by the API regardless of what the UI shows.
 */
export function rolesFromPayload(payload: Record<string, unknown>, rolesClaim: string): string[] {
  const direct = readClaim(payload, rolesClaim);
  if (Array.isArray(direct)) {
    return direct.map(String);
  }
  if (typeof direct === 'string') {
    return [direct];
  }
  const legacyRole = payload['role'];
  if (typeof legacyRole === 'string') {
    return [legacyRole];
  }
  if (Array.isArray(legacyRole)) {
    return legacyRole.map(String);
  }
  return [];
}
